import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@localagent/ui/components/button';
import { Badge } from '@localagent/ui/components/badge';
import { useCatalogEntry, useDefinitionVersions } from '../hooks/useIntegrations';
import { ActionPreview } from '../components/integrations/ActionPreview';
import { ConnectDialog } from '../components/integrations/ConnectDialog';
import { IntegrationLogo } from '../components/integrations/IntegrationLogo';
import type { IntegrationAction } from '../types/integrations-types';
import { LoadingState } from '@localagent/ui/components/shogun-spinner';
import { PageHeaderBar } from '../components/layout/PageHeaderBar';
import { BackButton } from '../components/layout/BackButton';

export function CatalogDetailView() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { data: entry, isLoading, error } = useCatalogEntry(slug);
  const [connectOpen, setConnectOpen] = useState(false);

  const definitionId = entry?.definition.id;
  const { data: allVersions } = useDefinitionVersions(definitionId);

  if (isLoading) {
    return (
      <LoadingState label="Loading integration..." className="h-full" />
    );
  }

  if (error || !entry) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <p className="text-destructive">Failed to load integration</p>
          <Button variant="outline" className="mt-4" onClick={() => void navigate('/connections')}>
            Back to Connections
          </Button>
        </div>
      </div>
    );
  }

  const { definition, latest_version } = entry;
  const rawActions = (latest_version?.dsl_content?.actions ?? []) as Array<{
    action_key: string; name: string; description: string; http_method: string; path_template: string;
    pagination?: Record<string, unknown>; response_transform?: Record<string, unknown>;
  }>;
  const actions: IntegrationAction[] = rawActions.map((a) => ({
    action_key: a.action_key,
    name: a.name,
    description: a.description,
    http_method: a.http_method as IntegrationAction['http_method'],
    path_template: a.path_template,
    pagination: a.pagination ?? null,
    response_transform: a.response_transform ?? null,
  }));

  const versionCount = allVersions?.length ?? (latest_version ? 1 : 0);

  return (
    <div className="flex h-full flex-col">
      <PageHeaderBar
        leading={
          <>
            <BackButton label="Back to connections" onClick={() => void navigate('/connections')} />
            <IntegrationLogo slug={definition.slug} name={definition.name} iconUrl={definition.icon_url} size={20} />
          </>
        }
        title={definition.name}
        adornment={
          <div className="flex shrink-0 items-center gap-1.5">
            <Badge variant="secondary">{definition.category.replace(/_/g, ' ')}</Badge>
            <Badge variant="outline">{definition.auth_type.replace(/_/g, ' ')}</Badge>
            {entry.has_active_connection && <Badge variant="success">Connected</Badge>}
            {entry.connection_count > 0 && (
              <Badge variant="outline">
                {entry.connection_count} connection{entry.connection_count !== 1 ? 's' : ''}
              </Badge>
            )}
          </div>
        }
        actions={
          <Button size="sm" onClick={() => setConnectOpen(true)} data-testid="connect-button">
            Connect
          </Button>
        }
      />

      <div className="flex-1 overflow-y-auto p-6">
        <div className="mx-auto max-w-4xl">
          {definition.description && (
            <p className="mb-6 text-body text-muted-foreground">{definition.description}</p>
          )}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="lg:col-span-2">
              {(latest_version?.auth_config as { mcp_url?: string } | undefined)?.mcp_url ? (
                <>
                  <h2 className="mb-3 text-title-2 font-semibold">Tools</h2>
                  <p className="text-body text-muted-foreground">
                    After you connect, your agent discovers this vendor&apos;s hosted MCP tools and can call them by name.
                    Available tools come from the live server rather than a fixed action list.
                  </p>
                </>
              ) : (
                <>
                  <h2 className="mb-3 text-title-2 font-semibold">Actions ({actions.length})</h2>
                  <ActionPreview actions={actions} />
                </>
              )}
            </div>
            <div>
              <h2 className="mb-3 text-title-2 font-semibold">Info</h2>
              <div className="rounded-lg bg-card p-4">
                <dl className="space-y-3 text-body">
                  <div>
                    <dt className="text-muted-foreground">Auth Type</dt>
                    <dd className="mt-0.5 font-medium">{definition.auth_type.replace(/_/g, ' ')}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Category</dt>
                    <dd className="mt-0.5 font-medium">{definition.category.replace(/_/g, ' ')}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Versions</dt>
                    <dd className="mt-0.5 font-medium">{versionCount}</dd>
                  </div>
                  {latest_version && (
                    <div>
                      <dt className="text-muted-foreground">Latest Version</dt>
                      <dd className="mt-0.5 font-medium">v{latest_version.version_number}</dd>
                    </div>
                  )}
                  <div>
                    <dt className="text-muted-foreground">Scope</dt>
                    <dd className="mt-0.5 font-medium">{definition.scope}</dd>
                  </div>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ConnectDialog
        definitionId={definition.id}
        definitionName={definition.name}
        authType={definition.auth_type}
        open={connectOpen}
        onOpenChange={setConnectOpen}
        onSuccess={(connectionId) => { void navigate(`/connections/${connectionId}`); }}
      />
    </div>
  );
}
